import type { ExportFileIssue, ExportGroupFailure } from '../types';

/**
 * The plugin reports the files that actually reached disk.  Keep this wording
 * based on that evidence so the UI never claims that a requested export exists.
 */
export interface ExportResultMessageInput {
  readonly success: boolean;
  readonly filePath?: string;
  readonly outputPath?: string;
  readonly resolvedOutputPath?: string;
  readonly filesWritten?: number;
  readonly successfulFiles?: readonly string[];
  readonly skippedFiles?: readonly string[];
  readonly fileIssues?: readonly ExportFileIssue[];
  readonly groupFailures?: readonly ExportGroupFailure[];
  readonly error?: string;
  readonly confirmedElements?: number;
  readonly elementsWritten?: number;
  readonly elementsExported?: number;
  readonly failedStep?: 'license' | 'calculate' | 'record' | 'write';
  readonly traceId?: string;
}

export type ExportResultMessageKind = 'success' | 'warning' | 'error';

export interface ExportResultMessage {
  readonly kind: ExportResultMessageKind;
  readonly message: string;
  readonly hasOutput: boolean;
}

function plural(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function sentence(value: string): string {
  const trimmed = value.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function uniqueFileNames(files: readonly string[] | undefined): string[] {
  return [...new Set((files ?? []).map((file) => file.trim()).filter(Boolean))];
}

function formatCreatedFiles(files: readonly string[], filesWritten?: number): string {
  if (files.length > 0) {
    return `Created ${plural(files.length, 'PCF file')}: ${files.join(', ')}.`;
  }
  if ((filesWritten ?? 0) > 0) {
    return `Created ${plural(filesWritten ?? 0, 'PCF file')}. The plugin did not report the file names.`;
  }
  return 'No PCF files were created.';
}

function formatOutputPath(input: ExportResultMessageInput): string {
  const path = input.resolvedOutputPath?.trim() || input.outputPath?.trim() || input.filePath?.trim();
  return path ? ` Output folder: ${path}.` : '';
}

function formatConfirmedElements(input: ExportResultMessageInput): string {
  const confirmed = input.confirmedElements ?? input.elementsWritten;
  if (typeof confirmed === 'number') {
    return ` ${plural(confirmed, 'export element')} confirmed and charged.`;
  }
  if (typeof input.elementsExported === 'number' && input.elementsExported > 0) {
    return ` ${plural(input.elementsExported, 'export element')} confirmed from files written.`;
  }
  return '';
}

function technicalDetail(input: ExportResultMessageInput, primaryReason: string): string {
  const rawError = input.error?.trim();
  if (!rawError || rawError === primaryReason.trim()) {
    return '';
  }
  return ` Technical detail: ${sentence(rawError)}`;
}

function getFailureDetail(input: ExportResultMessageInput): string {
  const issue = input.fileIssues?.find((item) => item.fatal) ?? input.fileIssues?.[0];
  if (issue) {
    const reason = issue.reason || `Could not write ${issue.fileName || 'a PCF file'}`;
    const action = issue.suggestedAction?.trim() ? ` Suggested action: ${sentence(issue.suggestedAction)}` : '';
    return ` Reason: ${sentence(reason)}${action}${technicalDetail(input, reason)}`;
  }

  const groupFailure = input.groupFailures?.[0];
  if (groupFailure) {
    const reason = groupFailure.error || `Could not export ${groupFailure.systemName}`;
    const action = groupFailure.suggestedAction?.trim()
      ? ` Suggested action: ${sentence(groupFailure.suggestedAction)}`
      : '';
    return ` Reason: ${sentence(reason)}${action}${technicalDetail(input, reason)}`;
  }

  return ` Reason: ${sentence(input.error || 'The export did not complete.')}`;
}

function formatSkippedSummary(input: ExportResultMessageInput): string {
  const skipped = input.skippedFiles?.length ?? 0;
  const failures = input.groupFailures?.length ?? 0;
  if (skipped === 0 && failures === 0 && (input.fileIssues?.length ?? 0) === 0) {
    return '';
  }
  const affected = skipped + failures || input.fileIssues!.length;
  return ` ${plural(affected, 'item')} could not be exported.${getFailureDetail(input)}`;
}

function traceSuffix(input: ExportResultMessageInput): string {
  return input.traceId ? ` Trace ID: ${input.traceId}.` : '';
}

/** Builds one auditable message for table and pick exports. */
export function getExportResultMessage(input: ExportResultMessageInput): ExportResultMessage {
  const files = uniqueFileNames(input.successfulFiles);
  const hasOutput = files.length > 0 || (input.filesWritten ?? 0) > 0;
  const output = formatOutputPath(input);
  const filesSummary = formatCreatedFiles(files, input.filesWritten);
  const hasProblems = (input.skippedFiles?.length ?? 0) > 0
    || (input.fileIssues?.length ?? 0) > 0
    || (input.groupFailures?.length ?? 0) > 0;

  if (input.success && hasOutput) {
    return {
      kind: hasProblems ? 'warning' : 'success',
      message: `${filesSummary}${output}${formatConfirmedElements(input)}${formatSkippedSummary(input)}${traceSuffix(input)}`,
      hasOutput,
    };
  }

  if (hasOutput) {
    const finalization = input.failedStep === 'record'
      ? ' The PCF files exist, but the export run could not be finalized; quota status may not be updated.'
      : ' The PCF files exist, but the export did not fully complete.';
    return {
      kind: 'warning',
      message: `${filesSummary}${output}${formatConfirmedElements(input)}${finalization}${getFailureDetail(input)}${traceSuffix(input)}`,
      hasOutput,
    };
  }

  return {
    kind: 'error',
    message: `${filesSummary}${output}${getFailureDetail(input)}${traceSuffix(input)}`,
    hasOutput: false,
  };
}
